import { useFieldContext } from './form';
import { Label } from '../ui/Label';
import { FieldInfo } from './FieldInfo';
import { parseISO, isValid } from 'date-fns';
import { DatePickerInput } from '../inputs/DatePicker.input';

interface DateFieldProps {
  label: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
}

/**
 * Parse date string to Date object
 */
const parseDateValue = (dateString?: string): Date | null => {
  if (!dateString) return null;

  try {
    // Try parsing as ISO string first, then fall back to Date constructor
    const date =
      dateString.includes('T') || dateString.includes('Z')
        ? parseISO(dateString)
        : new Date(dateString);

    // Check if date is valid
    if (!isValid(date)) return null;

    return date;
  } catch {
    return null;
  }
};

export function DateField({
  label,
  placeholder,
  required = false,
  disabled = false,
  minDate,
  maxDate,
}: DateFieldProps) {
  const field = useFieldContext<string>();

  // Convert the string value to Date object for DatePickerInput
  const dateValue = parseDateValue(field.state.value);

  // Handle date change - convert Date back to ISO string
  const handleDateChange = (date: Date | null) => {
    if (date) {
      field.handleChange(date.toISOString());
    } else {
      field.handleChange('');
    }
  };

  return (
    <div className="w-full">
      <Label htmlFor={label}>{label}</Label>
      <DatePickerInput
        value={dateValue}
        onChange={handleDateChange}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        minDate={minDate}
        maxDate={maxDate}
        inputClassName="min-h-[56px] h-auto bg-white border-gray-300 text-base md:text-sm px-3 py-2"
        error={
          field.state.meta.errors.length > 0
            ? field.state.meta.errors[0]
            : undefined
        }
      />
      <FieldInfo field={field} />
    </div>
  );
}
